<template>
    <main class="ticket-list-page relative flex-1 flex flex-col overflow-hidden bg-gray-50 focus:outline-none" tabindex="0">
        <section class="ticket-list-header bg-white border-b border-gray-200 flex-shrink-0">
            <div class="px-4 sm:px-6 lg:px-8 py-4">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div class="min-w-0">
                        <div class="flex items-center gap-3 min-w-0">
                            <div class="hidden sm:flex h-10 w-10 rounded-2xl bg-blue-100 text-blue-700 items-center justify-center shadow-sm">
                                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2z"/>
                                </svg>
                            </div>
                            <div class="min-w-0">
                                <h1 class="text-xl sm:text-2xl font-semibold text-gray-900 truncate">
                                    {{ $t('Tickets') }}
                                </h1>
                                <p class="hidden sm:block text-sm text-gray-500 mt-0.5">
                                    {{ $t('Manage ticket queues, replies, assignments, and approvals') }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 sm:gap-3">
                        <router-link
                            class="btn btn-blue shadow-sm rounded-lg flex-1 sm:flex-none text-center"
                            to="/dashboard/tickets/new"
                        >
                            {{ $t('Create ticket') }}
                        </router-link>
                        <button
                            type="button"
                            class="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition"
                            :title="$t('Refresh')"
                            @click="refreshCurrentView"
                        >
                            <svg-vue
                                :class="loadingTickets ? 'animate-spin' : ''"
                                class="w-4 h-4"
                                icon="font-awesome.sync-regular"
                            ></svg-vue>
                        </button>
                    </div>
                </div>

                <div class="mt-4 overflow-x-auto ticket-tabs-scroll">
                    <div class="inline-flex min-w-full sm:min-w-0 rounded-xl border border-gray-200 bg-gray-100 p-1 shadow-sm">
                        <button
                            :class="tabButtonClass('my')"
                            class="ticket-tab-button"
                            type="button"
                            @click="setView('my')"
                        >
                            <span>{{ $t('My tickets') }}</span>
                            <span
                                v-if="unreadByView.my > 0"
                                :class="currentView === 'my' ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'"
                                class="ticket-tab-badge"
                            >
                                {{ unreadByView.my > 99 ? '99+' : unreadByView.my }}
                            </span>
                        </button>

                        <button
                            :class="tabButtonClass('work')"
                            class="ticket-tab-button"
                            type="button"
                            @click="setView('work')"
                        >
                            <span>{{ $t('Work tickets') }}</span>
                            <span
                                v-if="unreadByView.work > 0"
                                :class="currentView === 'work' ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'"
                                class="ticket-tab-badge"
                            >
                                {{ unreadByView.work > 99 ? '99+' : unreadByView.work }}
                            </span>
                        </button>

                        <button
                            v-if="canApproveTicketing"
                            :class="tabButtonClass('approve')"
                            class="ticket-tab-button"
                            type="button"
                            @click="setView('approve')"
                        >
                            <span>{{ $t('Approve tickets') }}</span>
                            <span
                                v-if="unreadByView.approve > 0"
                                :class="currentView === 'approve' ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'"
                                class="ticket-tab-badge"
                            >
                                {{ unreadByView.approve > 99 ? '99+' : unreadByView.approve }}
                            </span>
                        </button>

                        <button
                            :class="tabButtonClass('cc')"
                            class="ticket-tab-button"
                            type="button"
                            @click="setView('cc')"
                        >
                            <span>{{ $t('CC tickets') }}</span>
                            <span
                                v-if="unreadByView.cc > 0"
                                :class="currentView === 'cc' ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'"
                                class="ticket-tab-badge"
                            >
                                {{ unreadByView.cc > 99 ? '99+' : unreadByView.cc }}
                            </span>
                        </button>
                    </div>
                </div>

                <div class="mt-4 rounded-2xl border border-blue-300 bg-blue-50 shadow-sm px-4 py-1.5 flex items-center gap-3">
                    <svg-vue class="h-5 w-5 text-blue-600 flex-shrink-0" icon="font-awesome.search-regular"></svg-vue>
                    <input
                        v-model.trim="currentFilters.search"
                        :placeholder="$t('Search')"
                        aria-label="Search"
                        class="form-input ticket-search-input block w-full flex-1 min-w-0 rounded-none border-0 bg-transparent px-0 py-2 text-sm text-gray-800 placeholder-blue-400 shadow-none focus:border-transparent focus:shadow-none"
                        type="text"
                        @input="onSearchInput"
                        @keyup.enter="applyFilters"
                    >
                    <button
                        v-if="currentFilters.search"
                        class="inline-flex items-center justify-center h-6 w-6 rounded-full text-blue-400 hover:text-blue-700 hover:bg-blue-200 flex-shrink-0 focus:outline-none"
                        :title="$t('Clear')"
                        type="button"
                        @click="clearSearch"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.times-light"></svg-vue>
                    </button>
                </div>

                <div class="mt-4 rounded-2xl border border-gray-200 bg-gray-50 shadow-sm overflow-hidden">
                    <div class="flex items-center justify-between gap-3 px-4 py-3 bg-white">
                        <div class="flex items-center gap-2 min-w-0">
                            <span class="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 01.8 1.6L14 13.67V19a1 1 0 01-.55.9l-4 2A1 1 0 018 21v-7.33L3.2 4.6A1 1 0 013 4z"/>
                                </svg>
                            </span>
                            <div class="min-w-0">
                                <div class="text-sm font-semibold text-gray-800">{{ $t('Filters') }}</div>
                                <div class="text-xs text-gray-500 truncate">
                                    {{ activeFilterLabel }}
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                            <button
                                v-if="anyFilter"
                                class="btn btn-white px-3 py-1.5 text-xs rounded-lg"
                                type="button"
                                @click="clearCurrentFilters"
                            >
                                {{ $t('Clear') }}
                            </button>
                            <button
                                class="btn btn-white px-3 py-1.5 text-xs rounded-lg"
                                type="button"
                                @click="toggleFilters"
                            >
                                {{ filtersExpanded ? $t('Hide filters') : $t('Show filters') }}
                            </button>
                        </div>
                    </div>

                    <div v-show="filtersExpanded" class="tickets-filter-panel border-t border-gray-200 bg-gray-50 p-4">
                        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                            <div>
                                <label class="ticket-filter-label">{{ $t('Ticket no') }}</label>
                                <input
                                    v-model.trim="currentFilters.ticket_no"
                                    :placeholder="$t('Ticket no')"
                                    class="form-input block w-full text-sm transition ease-in-out duration-150"
                                    @input="onTicketNoInput"
                                    @keyup.enter="applyFilters"
                                >
                            </div>
                            <div>
                                <label class="ticket-filter-label">{{ $t('Creator') }}</label>
                                <input-select
                                    v-model="currentFilters.customers"
                                    :options="customerList"
                                    option-label="name"
                                    option-sublabel="email"
                                    multiple
                                    searchable
                                    @change="applyFilters"
                                />
                            </div>
                            <div>
                                <label class="ticket-filter-label">{{ $t('Executors') }}</label>
                                <input-select
                                    v-model="currentFilters.agents"
                                    :options="agentList"
                                    multiple
                                    option-label="name"
                                    option-sublabel="email"
                                    searchable
                                    @change="applyFilters"
                                />
                            </div>
                            <div>
                                <label class="ticket-filter-label">{{ $t('Statuses') }}</label>
                                <input-select
                                    v-model="currentFilters.statuses"
                                    :options="statusList"
                                    multiple
                                    option-label="name"
                                    searchable
                                    @change="applyFilters"
                                />
                            </div>
                            <div>
                                <label class="ticket-filter-label">{{ $t('Sort') }}</label>
                                <div class="relative inline-flex w-full">
                                    <button
                                        class="relative -ml-px inline-flex items-center px-4 py-2 rounded-l-md border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700"
                                        :class="{'opacity-50 cursor-not-allowed': sort.column === 'default'}"
                                        :disabled="sort.column === 'default'"
                                        :title="sort.column === 'default' ? $t('Select a sort column to change the order direction') : ''"
                                        type="button"
                                        @click="changeSort"
                                    >
                                        <svg-vue
                                            v-show="sort.order === 'asc'"
                                            class="h-5 w-5 text-gray-400"
                                            icon="font-awesome.sort-amount-up-alt-regular"
                                        />
                                        <svg-vue
                                            v-show="sort.order === 'desc'"
                                            class="h-5 w-5 text-gray-400"
                                            icon="font-awesome.sort-amount-down-alt-regular"
                                        />
                                    </button>
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="-ml-px block form-select w-full pl-3 pr-9 py-2 rounded-l-none rounded-r-md border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                        @change="applyFilters"
                                    >
                                        <option value="default">{{ $t('Default') }}</option>
                                        <option value="subject">{{ $t('Subject') }}</option>
                                        <option value="status">{{ $t('Status') }}</option>
                                        <option value="updated_at">{{ $t('Updated at') }}</option>
                                        <option value="due_at">{{ $t('End date') }}</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label class="ticket-filter-label">{{ $t('Items per page') }}</label>
                                <input-select
                                    v-model="perPage"
                                    :options="[{id: 5, name: 5}, {id: 10, name: 10}, {id: 25, name: 25}, {id: 50, name: 50}]"
                                    option-label="name"
                                    required
                                    @change="applyFilters"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="tickets-list-body relative flex-1 min-h-0 overflow-hidden bg-gray-50">
            <loading :status="loadingTickets" />

            <div class="tickets-list h-full overflow-y-auto" :class="loadingTickets ? 'is-loading' : ''">
                <div class="hidden sm:block">
                    <div v-show="selectedRows.length > 0" v-on-clickaway="closeQuickActionDropdown" class="tickets-list-toolbar">
                        <button v-if="canDeleteTickets" class="btn hover:bg-gray-100 p-4 rounded-none" type="button" @click="toggleQuickActionDropdown('delete')">
                            <svg-vue class="h-6 w-6 text-gray-700" icon="font-awesome.trash-alt-regular"></svg-vue>
                        </button>
                    </div>
                </div>

                <template v-if="ticketList.length > 0">
                    <div class="sm:hidden p-3 space-y-3">
                        <router-link
                            v-for="ticket in ticketList"
                            :key="ticket.uuid"
                            :to="'/dashboard/tickets/' + ticket.uuid + '/manage'"
                            :class="ticket.deadline_urgent ? 'border-red-300 bg-red-50' : (ticket.unread_replies_count > 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white')"
                            class="block rounded-2xl border shadow-sm p-4 active:bg-blue-50 transition"
                        >
                            <div class="flex items-start gap-3">
                                <img
                                    :alt="$t('Avatar')"
                                    :src="ticket.user.avatar !== 'gravatar' ? ticket.user.avatar : ticket.user.gravatar"
                                    class="h-10 w-10 rounded-full flex-shrink-0 object-cover"
                                >
                                <div class="min-w-0 flex-1">
                                    <div class="flex items-start justify-between gap-2">
                                        <div class="min-w-0">
                                            <div class="flex items-center gap-1 flex-wrap mb-1">
                                                <span
                                                    v-for="label in ticket.labels"
                                                    :key="label.id"
                                                    :style="{backgroundColor: label.color}"
                                                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium leading-4 text-gray-100"
                                                >
                                                    {{ label.name }}
                                                </span>
                                            </div>
                                            <div class="text-sm font-semibold text-gray-900 leading-5">
                                                #{{ ticket.id }} - <span v-html="highlightMatches(ticket.subject)"></span>
                                            </div>
                                        </div>
                                        <span
                                            v-if="ticket.unread_replies_count > 0"
                                            class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold flex-shrink-0"
                                        >
                                            {{ ticket.unread_replies_count > 99 ? '99+' : ticket.unread_replies_count }}
                                        </span>
                                    </div>
                                    <div class="mt-1 text-sm text-gray-500 truncate" v-html="ticketSnippet(ticket)"></div>
                                    <div class="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500">
                                        <div class="min-w-0 truncate flex items-center gap-2">
                                            <span
                                                :class="ticket.executors_count > 0 ? 'inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-100' : 'text-gray-400'"
                                            >
                                                {{ ticket.executors_count > 0 ? (ticket.executors_count + ' ' + $t('Executor')) : $t('Unassigned') }}
                                            </span>
                                            <span
                                                v-if="ticket.started_at || ticket.due_at"
                                                :class="ticket.deadline_urgent ? 'font-semibold text-red-700' : ''"
                                            >
                                                <template v-if="ticket.started_at">{{ ticket.started_at | momentFormatDate }}</template>
                                                <template v-if="ticket.started_at && ticket.due_at"> → </template>
                                                <template v-if="ticket.due_at">{{ ticket.due_at | momentFormatDate }}</template>
                                            </span>
                                        </div>
                                        <div class="flex items-center gap-2 flex-shrink-0">
                                            <span class="inline-flex rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">
                                                {{ ticket.status ? ticket.status.name : $t('Unassigned') }}
                                            </span>
                                            <span>{{ ticket.updated_at | momentFormatDateTimeAgo }}</span>
                                            <button
                                                class="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-gray-300 bg-white text-gray-600 active:bg-gray-50"
                                                :class="isFollowUpDisabled(ticket) ? 'opacity-50' : ''"
                                                :disabled="isFollowUpDisabled(ticket)"
                                                :title="$t('Follow-up')"
                                                type="button"
                                                @click.stop.prevent="followUpTicket(ticket)"
                                            >
                                                <svg-vue
                                                    :class="followingUpTicketId === ticket.id ? 'animate-spin' : ''"
                                                    class="h-3.5 w-3.5"
                                                    :icon="followingUpTicketId === ticket.id ? 'font-awesome.sync-regular' : 'font-awesome.bell-regular'"
                                                ></svg-vue>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </router-link>
                    </div>

                    <div class="hidden sm:block p-4">
                        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-4 py-3 w-10">
                                        <input
                                            id="select-all-tickets"
                                            v-model="selectAll"
                                            aria-label="Checkbox"
                                            class="form-checkbox cursor-pointer h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                            type="checkbox"
                                            @change="selectAllTickets"
                                        >
                                    </th>
                                    <th class="hidden lg:table-cell px-4 py-3 text-left text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Creator') }}
                                    </th>
                                    <th class="px-4 py-3 text-left text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Ticket summary') }}
                                    </th>
                                    <th class="px-4 py-3 text-left text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Status') }}
                                    </th>
                                    <th class="px-4 py-3 text-center text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Executor') }}
                                    </th>
                                    <th class="hidden lg:table-cell px-4 py-3 text-left text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('End date') }}
                                    </th>
                                    <th class="px-4 py-3 text-left text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Updated at') }}
                                    </th>
                                    <th class="px-2 py-3 w-12 text-center text-xs leading-4 font-semibold text-gray-600 uppercase tracking-wider">
                                        {{ $t('Follow-up') }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-gray-100">
                                <router-link
                                    v-for="ticket in ticketList"
                                    :key="ticket.uuid"
                                    :to="'/dashboard/tickets/' + ticket.uuid + '/manage'"
                                    :class="ticketRowClass(ticket)"
                                    class="cursor-pointer transition"
                                    tag="tr"
                                >
                                    <td class="px-4 py-4 whitespace-no-wrap text-center text-sm leading-5 font-medium">
                                        <input
                                            :id="'ticket-' + ticket.id"
                                            v-model="selectedRows"
                                            :value="ticket.id"
                                            aria-label="Checkbox"
                                            class="form-checkbox cursor-pointer h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                            type="checkbox"
                                            @change="selectTicket"
                                            @click.stop
                                        >
                                    </td>
                                    <td class="hidden lg:table-cell px-4 py-4 whitespace-no-wrap leading-5">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="ticket.user.avatar !== 'gravatar' ? ticket.user.avatar : ticket.user.gravatar"
                                                    class="h-10 w-10 rounded-full object-cover"
                                                >
                                            </div>
                                            <div class="ml-4 min-w-0">
                                                <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                                                    {{ ticket.user.name }}
                                                </div>
                                                <div class="text-sm leading-5 text-gray-500 truncate">
                                                    {{ ticket.user.email }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-4 py-4 max-w-0 w-full whitespace-no-wrap">
                                        <div class="flex text-sm leading-5 text-gray-900 min-w-0">
                                            <span
                                                v-for="label in ticket.labels"
                                                :key="label.id"
                                                :style="{backgroundColor: label.color}"
                                                class="hidden lg:inline-flex items-center px-2 py-0.5 mr-1 rounded text-xs font-medium leading-4 text-gray-100 flex-shrink-0"
                                            >
                                                {{ label.name }}
                                            </span>
                                            <div class="w-full truncate font-medium">
                                                #{{ ticket.id }} - <span v-html="highlightMatches(ticket.subject)"></span>
                                                <span
                                                    v-if="ticket.unread_replies_count > 0"
                                                    class="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold"
                                                >
                                                    {{ ticket.unread_replies_count > 99 ? '99+' : ticket.unread_replies_count }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="text-sm leading-5 text-gray-500 w-full truncate mt-1" v-html="ticketSnippet(ticket)"></div>
                                    </td>
                                    <td class="px-4 py-4 whitespace-no-wrap leading-5">
                                        <div class="text-sm leading-5 font-medium text-gray-900">
                                            {{ ticket.status ? ticket.status.name : $t('Unassigned') }}
                                        </div>
                                    </td>
                                    <td class="px-4 py-4 whitespace-no-wrap leading-5 text-center">
                                        <div class="text-sm leading-5">
                                            <span
                                                :class="ticket.executors_count > 0 ? 'inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100' : 'text-gray-400'"
                                            >
                                                {{ ticket.executors_count > 0 ? ticket.executors_count : $t('Unassigned') }}
                                            </span>
                                        </div>
                                    </td>
                                    <td class="hidden lg:table-cell px-4 py-4 whitespace-no-wrap leading-5">
                                        <div
                                            v-if="ticket.started_at || ticket.due_at"
                                            :class="ticket.deadline_urgent ? 'text-sm font-semibold text-red-700' : 'text-sm text-gray-700'"
                                        >
                                            <span v-if="ticket.started_at">{{ ticket.started_at | momentFormatDate }}</span>
                                            <span v-if="ticket.started_at && ticket.due_at" class="text-gray-400 mx-1">–</span>
                                            <span v-if="ticket.due_at">{{ ticket.due_at | momentFormatDate }}</span>
                                        </div>
                                        <div v-else class="text-sm text-gray-400">—</div>
                                    </td>
                                    <td class="px-4 py-4 whitespace-no-wrap leading-5">
                                        <div class="text-sm text-gray-500">
                                            {{ ticket.updated_at | momentFormatDateTimeAgo }}
                                        </div>
                                    </td>
                                    <td class="px-2 py-4 whitespace-no-wrap leading-5 text-center">
                                        <button
                                            class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition"
                                            :class="isFollowUpDisabled(ticket) ? 'opacity-50 cursor-not-allowed' : ''"
                                            :disabled="isFollowUpDisabled(ticket)"
                                            :title="$t('Resend the latest chat notification to all participants?')"
                                            type="button"
                                            @click.stop.prevent="followUpTicket(ticket)"
                                        >
                                            <svg-vue
                                                :class="followingUpTicketId === ticket.id ? 'animate-spin' : ''"
                                                class="h-4 w-4"
                                                :icon="followingUpTicketId === ticket.id ? 'font-awesome.sync-regular' : 'font-awesome.bell-regular'"
                                            ></svg-vue>
                                        </button>
                                    </td>
                                </router-link>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>

                <template v-else-if="!loadingTickets">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full p-4 text-center">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl text-gray-900">{{ $t('No records found') }}</div>
                                </div>
                                <template v-if="anyFilter">
                                    <div class="flex justify-center items-center mt-1">
                                        <div class="text-gray-500">{{ $t('Try changing the filters, or rephrasing your search') }}.</div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </section>

        <nav class="ticket-pagination bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 flex-shrink-0">
            <div class="hidden sm:block">
                <p class="text-sm leading-5 text-gray-700">
                    {{ $t('Showing') }}
                    <span class="font-medium">{{ paginationStart }}</span>
                    {{ $t('to') }}
                    <span class="font-medium">{{ paginationEnd }}</span>
                    {{ $t('of') }}
                    <span class="font-medium">{{ pagination.total }}</span>
                    {{ $t('results') }}
                </p>
            </div>
            <div class="flex-1 flex justify-between sm:justify-end">
                <button
                    :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
                    :disabled="pagination.currentPage <= 1"
                    class="pagination-link"
                    type="button"
                    @click="changePage(pagination.currentPage - 1)"
                >
                    {{ $t('Previous') }}
                </button>
                <button
                    :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
                    :disabled="pagination.currentPage >= pagination.totalPages"
                    class="ml-3 pagination-link"
                    type="button"
                    @click="changePage(pagination.currentPage + 1)"
                >
                    {{ $t('Next') }}
                </button>
            </div>
        </nav>

        <div v-if="canDeleteTickets" v-show="quickActions.delete" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="quickActions.delete" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="quickActions.delete"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete tickets') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the selected tickets?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('All related data will be deleted') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="quickAction('delete')"
                            >
                                {{ $t('Delete tickets') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="quickActions.delete = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";

const VIEW_STORAGE_KEY = 'ticketing-ticket-list-last-view';
const STATE_STORAGE_KEY = 'ticketing-ticket-list-state-v3';
const ALLOWED_VIEWS = ['my', 'work', 'approve', 'cc'];

function defaultFilters() {
    return {
        ticket_no: '',
        search: '',
        customers: [],
        agents: [],
        // Default: semua status ditampilkan (tidak difilter).
        statuses: [],
    };
}

export default {
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Tickets')
        }
    },
    mixins: [clickaway],
    data() {
        return {
            loadingTickets: true,
            searchDebounceTimer: null,
            ticketsCancelSource: null,
            filtersByView: {
                my: defaultFilters(),
                work: defaultFilters(),
                approve: defaultFilters(),
                cc: defaultFilters(),
            },
            quickActions: {
                delete: false,
            },
            sort: {
                order: 'desc',
                column: 'default',
            },
            currentView: 'work',
            canApproveTicketing: false,
            unreadByView: {
                my: 0,
                work: 0,
                approve: 0,
                cc: 0,
            },
            page: 1,
            pageByView: {
                my: 1,
                work: 1,
                approve: 1,
                cc: 1,
            },
            perPage: 10,
            filtersExpanded: false,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
            agentList: [],
            customerList: [],
            statusList: [],
            ticketList: [],
            ticketsCacheByView: {
                my: null,
                work: null,
                approve: null,
                cc: null,
            },
            paginationCacheByView: {
                my: null,
                work: null,
                approve: null,
                cc: null,
            },
            selectAll: false,
            selectedRows: [],
            notificationChannel: null,
            // Follow-up: id tiket yang sedang diproses, dan waktu (ms)
            // sampai cooldown-nya berakhir per tiket agar tombol tidak
            // diklik berulang sebelum 10 menit.
            followingUpTicketId: null,
            followUpCooldownUntil: {},
            followUpTicker: null,
        }
    },
    computed: {
        currentFilters() {
            return this.filtersByView[this.currentView] || this.filtersByView.work;
        },
        canDeleteTickets() {
            return !!(this.$store.state.user && this.$store.state.user.role && this.$store.state.user.role.dashboard_access);
        },
        anyFilter() {
            return this.currentFilters.ticket_no !== ''
                || this.currentFilters.search !== ''
                || this.normalizeSelectedIds(this.currentFilters.customers).length !== 0
                || this.normalizeSelectedIds(this.currentFilters.agents).length !== 0
                || this.normalizeSelectedIds(this.currentFilters.statuses).length !== 0;
        },
        activeFilterLabel() {
            if (!this.anyFilter) {
                return this.$t('No active filters');
            }

            const parts = [];
            if (this.currentFilters.ticket_no) {
                parts.push(this.$t('Ticket no'));
            }
            if (this.currentFilters.search) {
                parts.push(this.$t('Search'));
            }
            if (this.normalizeSelectedIds(this.currentFilters.customers).length) {
                parts.push(this.$t('Creator'));
            }
            if (this.normalizeSelectedIds(this.currentFilters.agents).length) {
                parts.push(this.$t('Executors'));
            }
            if (this.normalizeSelectedIds(this.currentFilters.statuses).length) {
                parts.push(this.$t('Statuses'));
            }
            return parts.join(', ');
        },
        paginationStart() {
            if (!this.pagination.total) {
                return 0;
            }
            return (this.pagination.perPage * this.pagination.currentPage) - this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (!this.pagination.total) {
                return 0;
            }
            return this.pagination.perPage * this.pagination.currentPage <= this.pagination.total
                ? this.pagination.perPage * this.pagination.currentPage
                : this.pagination.total;
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
        momentFormatDate: function (value) {
            if (!value) {
                return '';
            }
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format);
        },
    },
    mounted() {
        this.restoreListState();

        const requestedView = this.$route.query && this.$route.query.view ? String(this.$route.query.view) : null;
        if (requestedView && ALLOWED_VIEWS.indexOf(requestedView) > -1) {
            this.currentView = requestedView;
        }

        this.page = this.pageByView[this.currentView] || 1;
        this.saveListState();

        this.getTickets();
        this.getFilters();
        this.getUnreadTabs();
        this.getNotificationSummary();
        window.addEventListener('dashboard-notifications-updated', this.onNotificationSummaryUpdated);
        this.startRealtimeSubscriptions();
    },
    beforeDestroy() {
        clearTimeout(this.searchDebounceTimer);
        if (this.ticketsCancelSource) {
            this.ticketsCancelSource.cancel('component destroyed');
        }
        window.removeEventListener('dashboard-notifications-updated', this.onNotificationSummaryUpdated);
        this.stopRealtimeSubscriptions();
        if (this.followUpTicker) {
            clearInterval(this.followUpTicker);
            this.followUpTicker = null;
        }
    },
    watch: {
        '$route.query.view'(value) {
            const requestedView = value ? String(value) : this.getStoredView();
            if (!ALLOWED_VIEWS.includes(requestedView) || requestedView === this.currentView) {
                return;
            }
            this.setView(requestedView, true);
        }
    },
    methods: {
        cloneFilters(filters) {
            return {
                ticket_no: filters && filters.ticket_no ? String(filters.ticket_no) : '',
                search: filters && filters.search ? String(filters.search) : '',
                customers: this.normalizeSelectedIds(filters && filters.customers ? filters.customers : []),
                agents: this.normalizeSelectedIds(filters && filters.agents ? filters.agents : []),
                statuses: this.normalizeSelectedIds(filters && filters.statuses ? filters.statuses : []),
            };
        },
        normalizeSelectedIds(values) {
            if (!Array.isArray(values)) {
                if (values === null || values === undefined || values === '') {
                    return [];
                }
                values = [values];
            }

            return values
                .map((value) => {
                    if (value && typeof value === 'object') {
                        return value.id;
                    }
                    return value;
                })
                .filter((value) => value !== null && value !== undefined && value !== '')
                .map((value) => {
                    const numberValue = Number(value);
                    return Number.isNaN(numberValue) ? value : numberValue;
                });
        },
        restoreListState() {
            let saved = null;
            try {
                saved = JSON.parse(window.localStorage ? window.localStorage.getItem(STATE_STORAGE_KEY) : null);
            } catch (error) {
                saved = null;
            }

            if (!saved || typeof saved !== 'object') {
                const storedView = this.getStoredView();
                if (storedView) {
                    this.currentView = storedView;
                }
                return;
            }

            if (saved.currentView && ALLOWED_VIEWS.indexOf(saved.currentView) > -1) {
                this.currentView = saved.currentView;
            }

            if (saved.filtersByView) {
                ALLOWED_VIEWS.forEach((view) => {
                    this.filtersByView[view] = this.cloneFilters(saved.filtersByView[view] || defaultFilters());
                });
            }

            if (saved.pageByView) {
                ALLOWED_VIEWS.forEach((view) => {
                    const page = parseInt(saved.pageByView[view], 10);
                    this.pageByView[view] = !Number.isNaN(page) && page > 0 ? page : 1;
                });
            }

            const allowedSortColumns = ['default', 'subject', 'status', 'updated_at', 'due_at'];
            if (saved.sort && ['asc', 'desc'].indexOf(saved.sort.order) > -1 && allowedSortColumns.indexOf(saved.sort.column) > -1) {
                this.sort = {
                    order: saved.sort.order,
                    column: saved.sort.column,
                };
            }

            const savedPerPage = parseInt(saved.perPage, 10);
            if ([5, 10, 25, 50].indexOf(savedPerPage) > -1) {
                this.perPage = savedPerPage;
            }

            this.filtersExpanded = !!saved.filtersExpanded;
        },
        saveListState() {
            if (!window.localStorage) {
                return;
            }

            const state = {
                currentView: this.currentView,
                filtersByView: {
                    my: this.cloneFilters(this.filtersByView.my),
                    work: this.cloneFilters(this.filtersByView.work),
                    approve: this.cloneFilters(this.filtersByView.approve),
                    cc: this.cloneFilters(this.filtersByView.cc),
                },
                pageByView: this.pageByView,
                sort: this.sort,
                perPage: this.normalizeSingleId(this.perPage) || 10,
                filtersExpanded: this.filtersExpanded,
            };

            window.localStorage.setItem(VIEW_STORAGE_KEY, this.currentView);
            window.localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(state));
        },
        getStoredView() {
            if (!window.localStorage) {
                return 'work';
            }
            const view = window.localStorage.getItem(VIEW_STORAGE_KEY);
            return ALLOWED_VIEWS.indexOf(view) > -1 ? view : 'work';
        },
        normalizeSingleId(value) {
            if (value && typeof value === 'object') {
                value = value.id;
            }
            const numberValue = Number(value);
            return Number.isNaN(numberValue) ? value : numberValue;
        },
        getFilterParams(filters) {
            return {
                ticket_no: filters.ticket_no,
                search: filters.search,
                customers: this.normalizeSelectedIds(filters.customers),
                agents: this.normalizeSelectedIds(filters.agents),
                statuses: this.normalizeSelectedIds(filters.statuses),
            };
        },
        tabButtonClass(view) {
            return this.currentView === view
                ? 'bg-white text-blue-700 shadow-sm border-blue-100'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white border-transparent';
        },
        toggleFilters() {
            this.filtersExpanded = !this.filtersExpanded;
            this.saveListState();
        },
        clearCurrentFilters() {
            this.filtersByView[this.currentView] = defaultFilters();
            this.applyFilters();
        },
        clearSearch() {
            if (this.currentFilters.search === '') {
                return;
            }
            this.currentFilters.search = '';
            this.applyFilters();
        },
        // Pencarian realtime: debounce 300ms setelah user berhenti mengetik,
        // lalu terapkan filter otomatis tanpa perlu menekan Enter.
        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.searchDebounceTimer = null;
                this.applyFilters();
            }, 300);
        },
        onTicketNoInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.searchDebounceTimer = null;
                this.applyFilters();
            }, 300);
        },
        applyFilters() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
            this.page = 1;
            this.pageByView[this.currentView] = 1;
            this.selectedRows = [];
            this.selectAll = false;
            this.invalidateCurrentCache();
            this.saveListState();
            this.getTickets();
        },
        invalidateCurrentCache() {
            this.ticketsCacheByView[this.currentView] = null;
            this.paginationCacheByView[this.currentView] = null;
        },
        closeQuickActionDropdown() {
            this.quickActions.delete = false;
        },
        toggleQuickActionDropdown(quickAction) {
            this.quickActions.delete = quickAction === 'delete' ? !this.quickActions.delete : false;
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.pageByView[this.currentView] = page;
                this.saveListState();
                this.getTickets();
            }
        },
        changeSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.invalidateCurrentCache();
            this.saveListState();
            this.getTickets();
        },
        selectTicket() {
            this.closeQuickActionDropdown();
            this.selectAll = this.selectedRows.length === this.ticketList.length;
        },
        selectAllTickets() {
            this.selectedRows = [];
            this.closeQuickActionDropdown();
            if (this.selectAll) {
                this.ticketList.forEach((ticket) => {
                    this.selectedRows.push(ticket.id);
                });
            }
        },
        startRealtimeSubscriptions() {
            this.stopRealtimeSubscriptions();
            if (!window.Echo || !this.$store.state.user) {
                return;
            }
            this.notificationChannel = window.Echo.private('users.' + this.$store.state.user.id + '.tickets')
                .listen('.ticket.updated', () => {
                    if (this.selectedRows.length > 0) {
                        return;
                    }
                    this.getTickets(true);
                    this.getUnreadTabs();
                    this.getNotificationSummary();
                });
        },
        stopRealtimeSubscriptions() {
            if (window.Echo && this.$store.state.user) {
                window.Echo.leave('private-users.' + this.$store.state.user.id + '.tickets');
            }
            this.notificationChannel = null;
        },
        refreshCurrentView() {
            this.invalidateCurrentCache();
            this.getTickets();
            this.getUnreadTabs();
            this.getNotificationSummary();
        },
        getTickets(silent = false) {
            const filters = this.getFilterParams(this.currentFilters);
            const view = this.currentView;
            const page = this.page;
            const perPage = this.normalizeSingleId(this.perPage) || 10;

            // Batalkan request sebelumnya: saat search realtime, request lama
            // yang belum selesai tidak hanya menumpuk di antrean server, tapi
            // responsnya bisa datang terakhir dan menimpa hasil terkini.
            if (this.ticketsCancelSource) {
                this.ticketsCancelSource.cancel('superseded');
            }
            this.ticketsCancelSource = axios.CancelToken.source();

            if (!silent) {
                this.selectAll = false;
                this.selectedRows = [];
                this.loadingTickets = true;
            }

            axios.get('api/dashboard/tickets', {
                cancelToken: this.ticketsCancelSource.token,
                params: {
                    _t: Date.now(),
                    page: page,
                    sort: this.sort,
                    perPage: perPage,
                    ticket_no: filters.ticket_no,
                    search: filters.search,
                    customers: filters.customers,
                    agents: filters.agents,
                    statuses: filters.statuses,
                    view: view,
                }
            }).then((response) => {
                if (view !== this.currentView) {
                    return;
                }

                this.ticketList = response.data.items;
                this.pagination = response.data.pagination;
                this.ticketsCacheByView[view] = response.data.items;
                this.paginationCacheByView[view] = response.data.pagination;

                if (response.data.unread_tabs) {
                    this.unreadByView = response.data.unread_tabs;
                }

                if (this.pagination.totalPages > 0 && this.pagination.totalPages < this.pagination.currentPage) {
                    this.page = this.pagination.totalPages;
                    this.pageByView[this.currentView] = this.pagination.totalPages;
                    this.saveListState();
                    this.getTickets();
                    return;
                }

                if (this.ticketList.length === 0) {
                    this.selectAll = false;
                    this.selectedRows = [];
                }

                this.loadingTickets = false;
            }).catch((error) => {
                // Request yang dibatalkan bukan error — request penggantinya
                // yang akan mematikan state loading.
                if (axios.isCancel(error)) {
                    return;
                }
                if (view === this.currentView) {
                    this.loadingTickets = false;
                }
            });
        },
        getFilters() {
            axios.get('api/dashboard/tickets/filters').then((response) => {
                this.agentList = response.data.agents;
                this.customerList = response.data.customers;
                this.statusList = response.data.statuses;
                this.canApproveTicketing = !!response.data.can_approve_ticketing;
                if (!this.canApproveTicketing && this.currentView === 'approve') {
                    this.setView('work');
                }
            });
        },
        getNotificationSummary() {
            axios.get('api/dashboard/notifications', {params: {limit: 1}}).then((response) => {
                this.$store.commit('setNotificationUnreadTotal', response.data.unread_total || 0);
            });
        },
        getUnreadTabs() {
            axios.get('api/dashboard/tickets/unread-tabs').then((response) => {
                if (response.data && response.data.unread_tabs) {
                    this.unreadByView = response.data.unread_tabs;
                }
            });
        },
        onNotificationSummaryUpdated(event) {
            const payload = event && event.detail ? event.detail : null;
            if (!payload || !payload.unread_by_view) {
                if (payload && Object.prototype.hasOwnProperty.call(payload, 'unread_total')) {
                    this.$store.commit('setNotificationUnreadTotal', payload.unread_total || 0);
                }
                return;
            }
            this.$store.commit('setNotificationUnreadTotal', payload.unread_total || 0);
            this.getUnreadTabs();
        },
        setView(view, fromRouteWatcher = false) {
            if (!ALLOWED_VIEWS.includes(view)) {
                view = 'work';
            }
            if (view === 'approve' && !this.canApproveTicketing) {
                view = 'work';
            }

            if (this.currentView === view && !fromRouteWatcher) {
                return;
            }

            this.pageByView[this.currentView] = this.page;
            this.currentView = view;
            this.page = this.pageByView[view] || 1;
            this.selectAll = false;
            this.selectedRows = [];
            this.closeQuickActionDropdown();
            this.saveListState();

            const cachedTickets = this.ticketsCacheByView[view];
            const cachedPagination = this.paginationCacheByView[view];
            if (cachedTickets && cachedPagination) {
                this.ticketList = cachedTickets;
                this.pagination = cachedPagination;
                this.loadingTickets = false;
                this.getTickets(true);
            } else {
                this.ticketList = [];
                this.getTickets();
            }

            if (!fromRouteWatcher) {
                this.$router.replace({path: '/dashboard/tickets', query: {view: view}}).catch(function () {});
            }
        },
        quickAction(param, value) {
            if (param === 'delete' && !this.canDeleteTickets) {
                return;
            }
            axios.post('api/dashboard/tickets/quick-actions', {
                action: param,
                value: value,
                tickets: this.selectedRows,
            }).then((response) => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: response.data.message,
                    type: 'success'
                });
                this.invalidateCurrentCache();
                this.getTickets();
                this.getUnreadTabs();
                this.getNotificationSummary();
                this.closeQuickActionDropdown();
            }).catch(() => {
                this.closeQuickActionDropdown();
            });
        },
        /**
         * Tombol follow-up per baris: kirim ulang notifikasi chat terbaru
         * tiket ke semua peserta. Server membatasi 1x per 10 menit, jadi
         * setelah berhasil tombol dikunci selama cooldown.
         */
        followUpTicket(ticket) {
            if (this.isFollowUpDisabled(ticket)) {
                return;
            }
            if (!window.confirm(this.$i18n.t('Resend the latest chat notification to all participants?').toString())) {
                return;
            }

            this.followingUpTicketId = ticket.id;
            axios.post('api/dashboard/tickets/' + ticket.uuid + '/follow-up')
                .then(() => {
                    this.startFollowUpCooldown(ticket.id, 10);
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: this.$i18n.t('Follow-up notification sent').toString(),
                        type: 'success'
                    });
                })
                .catch((error) => {
                    const response = error.response;
                    if (response && response.status === 429) {
                        const minutes = response.data.retry_after_minutes;
                        this.startFollowUpCooldown(ticket.id, minutes);
                        this.$notify({
                            title: this.$i18n.t('Error').toString(),
                            text: this.$i18n.t('Please wait {minutes} more minutes before following up again', { minutes: minutes }).toString(),
                            type: 'error'
                        });
                        return;
                    }
                    this.$notify({
                        title: this.$i18n.t('Error').toString(),
                        text: (response && response.data && response.data.message) ||
                            this.$i18n.t('An error occurred while saving data').toString(),
                        type: 'error'
                    });
                })
                .finally(() => {
                    this.followingUpTicketId = null;
                });
        },
        isFollowUpDisabled(ticket) {
            if (this.followingUpTicketId === ticket.id) {
                return true;
            }
            const until = this.followUpCooldownUntil[ticket.id];
            return !!until && Date.now() < until;
        },
        startFollowUpCooldown(ticketId, minutes) {
            const remaining = parseInt(minutes, 10);
            if (!remaining || remaining < 1) {
                return;
            }
            // $set dipakai karena key baru pada objek biasa tidak reaktif di Vue 2.
            this.$set(this.followUpCooldownUntil, ticketId, Date.now() + remaining * 60000);
            this.ensureFollowUpTicker();
        },
        ensureFollowUpTicker() {
            if (this.followUpTicker) {
                return;
            }
            // Ticker hanya untuk memicu render ulang agar tombol kembali
            // aktif tepat saat cooldown habis.
            this.followUpTicker = setInterval(() => {
                const now = Date.now();
                const active = Object.keys(this.followUpCooldownUntil)
                    .filter((id) => this.followUpCooldownUntil[id] > now);
                if (active.length === 0) {
                    clearInterval(this.followUpTicker);
                    this.followUpTicker = null;
                }
                this.$forceUpdate();
            }, 30000);
        },
        plainText(value) {
            const div = document.createElement('div');
            div.innerHTML = value || '';
            return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
        },
        escapeHtml(value) {
            const div = document.createElement('div');
            div.textContent = value === null || value === undefined ? '' : String(value);
            return div.innerHTML;
        },
        // Highlight kata kunci pencarian pada teks (case-insensitive), input di-escape dulu.
        highlightMatches(value) {
            const keyword = (this.currentFilters.search || '').trim();
            const escaped = this.escapeHtml(value);
            if (!keyword) {
                return escaped;
            }
            const safeKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return escaped.replace(new RegExp('(' + safeKeyword + ')', 'gi'), '<mark class="ticket-search-highlight">$1</mark>');
        },
        // Saat pencarian aktif, tampilkan cuplikan di sekitar kata yang cocok (dari backend).
        ticketSnippet(ticket) {
            const keyword = (this.currentFilters.search || '').trim();
            if (keyword && ticket.search_snippet) {
                return this.highlightMatches(ticket.search_snippet);
            }
            const body = this.plainText(ticket.lastReply ? ticket.lastReply.body : '');
            return body ? this.highlightMatches(body) : this.escapeHtml(this.$t('No replies yet'));
        },
        ticketRowClass(ticket) {
            if (ticket.deadline_urgent) {
                return 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500';
            }
            if (ticket.unread_replies_count > 0) {
                return 'bg-blue-50 hover:bg-blue-100';
            }
            return 'bg-white hover:bg-gray-50';
        },
    }
}
</script>

<style lang="scss">
.ticket-list-page {
    min-height: 0;
}

.ticket-list-header {
    z-index: 1;
}

.ticket-tabs-scroll {
    scrollbar-width: none;
}

.ticket-tabs-scroll::-webkit-scrollbar {
    display: none;
}

.ticket-tab-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 0.5rem;
    border-width: 1px;
    transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
    transition-duration: 150ms;
    white-space: nowrap;
    flex: 1 1 0%;
}

@media (min-width: 640px) {
    .ticket-tab-button {
        padding-left: 1rem;
        padding-right: 1rem;
        flex: none;
    }
}

.ticket-tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
}

.ticket-filter-label {
    display: block;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
}

.tickets-list-body {
    min-height: 0;
}

.tickets-list {
    background-color: #f9fafb;
    width: 100%;
    z-index: 0;
    overflow-y: auto;
    flex: 1 1 0%;
    min-height: 0;
    transition: opacity 0.15s ease;
}

.tickets-list.is-loading {
    opacity: 0.55;
}

.ticket-search-highlight {
    background-color: #fef08a;
    color: #78350f;
    border-radius: 0.125rem;
    padding: 0 0.125rem;
}

/* Paksa rendering terang untuk input search agar browser dark-mode
   tidak menampilkan background/caret hitam pada input native. */
.ticket-search-input {
    appearance: none;
    -webkit-appearance: none;
    background-color: #ffffff;
    color: #1f2937;
    caret-color: #2563eb;
}

.filters-options {
    border-radius: 0.375rem;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    padding: 0.75rem;
    overflow-y: auto;
    height: calc(100vh - 270px);
    max-height: 360px;
}

@media (min-width: 768px) {
    .filters-options {
        height: calc(100vh - 140px)
    }
}

.tickets-filter-panel {
    overflow-y: auto;
    max-height: 44vh;
    -webkit-overflow-scrolling: touch;
}

.tickets-list-toolbar {
    background-color: #ffffff;
    position: fixed;
    display: flex;
    border-radius: 0.375rem;
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -2px rgba(15, 23, 42, 0.05);
    border: 1px solid #e5e7eb;
    overflow: visible;
    left: 50px;
    z-index: 30;
}

.ticket-pagination {
    z-index: 1;
}

@media (min-width: 768px) {
    .tickets-list-toolbar {
        left: 50%;
    }
}

@media (max-width: 640px) {
    .ticket-list-header .btn {
        font-size: 0.875rem;
    }

    .ticket-tab-button {
        min-width: 9.5rem;
    }

    .tickets-filter-panel {
        max-height: 50vh;
    }
}
</style>
